import React, { useEffect, useRef } from "react";
import "./App.css";
import { OverviewMap } from "./features/aircraft/OverviewMap";
import { Box } from "@mui/system";
import "@fontsource/poppins";
import { Auth } from "aws-amplify";
import { configure, logUserInfo, setAuthListener } from "./aws";
import { NotificationSnackBar } from "./features/notification/NotificationSnackBar";
import { Websockets } from "./features/aircraft/Websockets";
import { useAppDispatch, useAppSelector } from "./app/hooks";
import { selectConnections } from "./features/aircraft/aircraftSelectors";
import { useInterval } from "./useInterval";
import {
  getCellsThunk,
  getRegionsThunk,
  selectRegion,
} from "./features/session/sessionSlice";
import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";
import { getCassettesThunk } from "./features/mission/missionSlice";
import { resetConnections } from "./features/aircraft/aircraftSlice";
import { VideoTest } from "./VideoTest";
import { useLocation } from "react-router-dom";
import { getAircraftListThunk } from "./features/aircraft/aircraftActions";

datadogRum.init({
  applicationId: "456bc3a8-7610-4d33-80ef-758a0939e306",
  clientToken: "pub25d95700cd1ef9e09433c0e3ee822355",
  site: "datadoghq.eu",
  service: "pilot-dashboard",
  version: process.env.REACT_APP_BASE_HASH,
  sampleRate: 100,
  premiumSampleRate: 100,
  trackInteractions: true,
});

datadogLogs.init({
  clientToken: "pub25d95700cd1ef9e09433c0e3ee822355",
  site: "datadoghq.eu",
  service: "pilot-dashboard",
  version: process.env.REACT_APP_BASE_HASH,
  forwardErrorsToLogs: true,
  sampleRate: 100,
  env: process.env.REACT_APP_ENVIRONMENT,
});

datadogRum.startSessionReplayRecording();
configure();
function App() {
  const dispatch = useAppDispatch();
  const connections = useAppSelector(selectConnections);
  const region = useAppSelector(selectRegion);
  const blockingUnload = useRef<boolean>(false);
  const configureAws = async () => {
    setAuthListener();
  };
  useEffect(() => {
    dispatch(getCassettesThunk());
    configureAws();
    logUserInfo();
    dispatch(getRegionsThunk());
  }, []);
  useEffect(() => {
    dispatch(resetConnections());
    Websockets.getInstance().disconnectAllUavs();
    dispatch(getAircraftListThunk({}));
    return () => {};
  }, []);

  useEffect(() => {
    region?.regionName && dispatch(getCellsThunk(region?.regionName));
  }, [region]);

  useInterval(() => {
    dispatch(getAircraftListThunk({}));
  }, 20000);

  // Session keep-alive: force refresh auth tokens to prevent session expiry
  // Refresh interval is (expiry / 2) - 1 minute, allowing a retry if refresh fails
  const SESSION_EXPIRY_MINUTES = 60;
  const SESSION_REFRESH_INTERVAL_MS = (SESSION_EXPIRY_MINUTES / 2 - 1) * 60 * 1000;
  
  useInterval(async () => {
    try {
      const session = await Auth.currentSession();
      // Force refresh by calling refreshSession with the current refresh token
      const refreshToken = session.getRefreshToken();
      const user = await Auth.currentAuthenticatedUser();
      await new Promise<void>((resolve, reject) => {
        user.refreshSession(refreshToken, (err: Error | null) => {
          if (err) reject(err);
          else resolve();
        });
      });
    } catch (err) {
      console.warn("Session refresh failed:", err);
    }
  }, SESSION_REFRESH_INTERVAL_MS);

  useEffect(() => {
    if (
      Object.keys(connections).length > 0 &&
      blockingUnload.current === false
    ) {
      window.addEventListener("beforeunload", beforeUnload);
      blockingUnload.current = true;
    } else {
      window.removeEventListener("beforeunload", beforeUnload);
      blockingUnload.current = false;
    }
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      blockingUnload.current = false;
    };
  }, [Object.keys(connections).length]);

  const beforeUnload = (event: any) => {
    Websockets.getInstance().disconnectAllUavs();
    dispatch(resetConnections());
  };

  useEffect(() => {
    const disableContextMenu = (e: Event) => {
      e.preventDefault();
    };

    document.addEventListener("contextmenu", disableContextMenu);

    return () => {
      document.removeEventListener("contextmenu", disableContextMenu);
    };
  }, []);

  const location = useLocation();

  return (
    <div className="App">
      {location.pathname === "/video-test" ? (
        <VideoTest />
      ) : (
        <Box
          position={"relative"}
          sx={{
            height: "100vh",
            width: "100vw",
          }}
        >
          <Box
            position={"absolute"}
            sx={{
              height: "100vh",
              width: "100vw",
              overflow: "hidden",
            }}
          >
            <OverviewMap />
            <NotificationSnackBar />
          </Box>
        </Box>
      )}
    </div>
  );
}

export default App;
